"use client";
import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from "react";
import {
  CustomEventDataType,
  CustomEventType,
  CustomEventErrorType,
  SSE,
  SSEOptions,
  SSEOptionsMethod,
} from "@patron/utils/helpers";
export type NotificationMessageEvent = string;
export const HeartbeatEvent: NotificationMessageEvent = "heartbeat";
export const MessageEvent: NotificationMessageEvent = "message";
export type EventMessageEventType = "CONTENT" | "GENERIC" | "STATUS_TRANSITION" | "CONTENT_NOTE" | "ERROR" | "MUTATION";
export interface Notification {
  id: string;
  event: "heartbeat" | "message" | "unreadCount";
  subject: string;
  pagePath: string;
  eventMessage: {
    source: string;
    loginID: string;
    domain: string;
    entity: string;
    event: string;
    key: number;
    childKey?: number;
    createdAt: number;
    eventType: string;
    summary: string;
    type: EventMessageEventType;
    data?: any;
    read?: boolean;
  };
}

const NOTIFICATIONS_ENABLED = process.env.NEXT_PUBLIC_NOTIFICATIONS_ENABLED === "true";

// ---------------------------------------------------------------------------
// Notification Manager - Pure Singleton Outside React
// ---------------------------------------------------------------------------
/**
 * A handler function that processes notifications
 */
type NotificationHandler = (notification: Notification) => void;
/**
 * A subscription object that includes handler and optional subjects filter
 */
interface Subscription {
  id: string;
  handler: NotificationHandler;
  subjects: string[];
}
/**
 * Notification manager singleton that exists outside of React's render cycle
 */
class NotificationManagerSingleton {
  private static instance: NotificationManagerSingleton;
  private subscriptions: Subscription[] = [];
  private sseConnection: SSE | null = null;
  private connectionStatus = false;
  private connectionListeners: Set<(status: boolean) => void> = new Set();
  private retryInterval = 5000;
  private maxRetryInterval = 30000; // Maximum wait between retries (30 seconds)
  private maxRetries = 0; // Maximum number of retry attempts | 0 means infinite
  private currentRetry = 0;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  // Store the connection options for reconnection
  private _connectOptions: { endpoint: string; headers?: { [key: string]: string }; withCredentials?: boolean } | null = null;
  private constructor() {}
  /**
   * Get the singleton instance
   */
  public static getInstance(): NotificationManagerSingleton {
    if (!NotificationManagerSingleton.instance) {
      NotificationManagerSingleton.instance = new NotificationManagerSingleton();
    }
    return NotificationManagerSingleton.instance;
  }
  /**
   * Set up a connection to the SSE server
   */
  public connect(options: { endpoint: string; headers?: { [key: string]: string }; withCredentials?: boolean }): void {
    if (!NOTIFICATIONS_ENABLED) return;
    if (this.sseConnection) return;
    // Store the options for reconnection
    this._connectOptions = options;
    const defaultHeaders = {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    };
    const sseOptions: SSEOptions = {
      method: SSEOptionsMethod.GET,
      headers: { ...defaultHeaders, ...options.headers },
      withCredentials: options.withCredentials ?? true,
      payload: JSON.stringify({
        clientId: crypto.randomUUID?.() || `client-${Date.now()}`,
        timestamp: Date.now(),
      }),
    };
    this.sseConnection = new SSE(options.endpoint, sseOptions);
    // Set up event listeners
    this.sseConnection.addEventListener(HeartbeatEvent, (event: CustomEventType) => {
      // Reset retry counter on successful heartbeat
      this.currentRetry = 0;
    });
    this.sseConnection.addEventListener("unreadCount", (event: CustomEventType) => {
      const dataEvent = event as CustomEventDataType;
      try {
        const payload = JSON.parse(dataEvent.data);
        const notification: Notification = {
          id: payload.id || dataEvent.id || Date.now().toString(),
          event: payload.event || dataEvent.type,
          subject: payload.subject || "",
          eventMessage: payload.eventMessage,
          pagePath: payload.pagePath || "",
        };

        this.notifySubscribers(notification);
        // Reset retry counter on successful message
        this.currentRetry = 0;
      } catch (error) {
        console.error("Failed to parse SSE notification payload:", error);
      }
    });
    this.sseConnection.addEventListener("open", () => {
      console.log("SSE connection established");
      this.setConnectionStatus(true);
      // Reset retry counter on successful connection
      this.currentRetry = 0;
      // Clear any pending reconnection timeout
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
    });
    this.sseConnection.addEventListener("error", (event: CustomEventType) => {
      const errorEvent = event as CustomEventErrorType;
      // console.log("SSE connection error:", errorEvent);
      this.setConnectionStatus(false);
      // Schedule reconnection with exponential backoff
      this.scheduleReconnect();
    });
    // Start connection
    this.sseConnection.stream();
  }
  /**
   * Close the SSE connection
   */
  public disconnect(): void {
    // Cancel any pending reconnection
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.sseConnection) {
      this.sseConnection.close();
      this.sseConnection = null;
      this.setConnectionStatus(false);
    }
    // Reset retry counter
    this.currentRetry = 0;
  }
  /**
   * Get current connection status
   */
  public isConnected(): boolean {
    return this.connectionStatus;
  }
  /**
   * Subscribe to connection status changes
   */
  public onConnectionChange(listener: (status: boolean) => void): () => void {
    this.connectionListeners.add(listener);
    // Immediately call with current status
    listener(this.connectionStatus);
    // Return unsubscribe function
    return () => {
      this.connectionListeners.delete(listener);
    };
  }
  /**
   * Subscribe to notifications with optional subject filtering
   */
  public subscribe(handler: NotificationHandler, subjects: string[] = []): () => void {
    const id = crypto.randomUUID?.() || `sub-${Date.now()}-${Math.random()}`;
    const subscription: Subscription = {
      id,
      handler,
      subjects,
    };
    this.subscriptions.push(subscription);
    // Return unsubscribe function
    return () => {
      this.subscriptions = this.subscriptions.filter((sub) => sub.id !== id);
    };
  }
  /**
   * Notify all subscribers of a new notification, filtering by subject
   */
  private notifySubscribers(notification: Notification): void {
    for (const subscription of this.subscriptions) {
      // Skip if subjects don't match (empty subjects array means "all subjects")
      if (subscription.subjects.length > 0 && notification.subject && !subscription.subjects.includes(notification.subject)) {
        continue;
      }
      // Call the handler
      try {
        subscription.handler(notification);
      } catch (error) {
        console.error("Error in notification handler:", error);
      }
    }
  }
  /**
   * Update connection status and notify listeners
   */
  private setConnectionStatus(status: boolean): void {
    this.connectionStatus = status;
    this.connectionListeners.forEach((listener) => {
      try {
        listener(status);
      } catch (error) {
        console.error("Error in connection status listener:", error);
      }
    });
  }
  /**
   * Schedule a reconnection attempt with exponential backoff
   */
  private scheduleReconnect(): void {
    // Cancel any existing reconnection timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    // Check if we've exceeded maximum retry attempts
    if (this.currentRetry >= this.maxRetries && this.maxRetries !== 0) {
      console.error(`Maximum reconnection attempts (${this.maxRetries}) reached. Giving up.`);
      return;
    }
    // Calculate backoff time with exponential increase
    const backoff = Math.min(this.retryInterval * Math.pow(1.5, this.currentRetry), this.maxRetryInterval);
    console.log(`Scheduling reconnection attempt ${this.currentRetry + 1}/${this.maxRetries} in ${backoff}ms`);
    // Schedule reconnection
    this.reconnectTimeout = setTimeout(() => {
      const retryCount = this.maxRetries === 0 ? "infinite" : `${this.currentRetry + 1}/${this.maxRetries}`;
      console.log(`Attempting to reconnect (${retryCount})...`);
      this.currentRetry++;
      // Close existing connection if any
      if (this.sseConnection) {
        this.sseConnection.close();
        this.sseConnection = null;
      }
      // Reconnect using stored options
      if (this._connectOptions) {
        this.connect(this._connectOptions);
      } else {
        console.error("Cannot reconnect: No connection options stored");
      }
    }, backoff);
  }
}
// Get singleton instance
const NotificationManager = NotificationManagerSingleton.getInstance();
// ---------------------------------------------------------------------------
// React Context and Hooks for Notifications
// ---------------------------------------------------------------------------
interface NotificationContextValue {
  connected: boolean;
  notifications: Notification[];
  updateNotificatiosnRecieved: () => void;
  notificationReceived: boolean;
}
interface NotificationProviderProps {
  children: ReactNode;
  endpoint: string;
  headers?: { [key: string]: string };
  withCredentials?: boolean;
}

const DisabledNotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => (
  <NotificationContext.Provider
    value={{
      connected: false,
      notifications: [],
      notificationReceived: false,
      updateNotificatiosnRecieved: () => {},
    }}
  >
    {children}
  </NotificationContext.Provider>
);

const NotificationContext = createContext<NotificationContextValue | null>(null);
export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  endpoint,
  headers,
  withCredentials = true,
}) => {
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notficationRecieved, setNotficationRecieved] = useState(false);
  // Connect to SSE on mount
  useEffect(() => {
    // Listen for connection changes
    const unsubscribe = NotificationManager.onConnectionChange(setConnected);

    // Connect to SSE
    NotificationManager.connect({
      endpoint,
      headers,
      withCredentials,
    });
    // Disconnect on unmount
    return () => {
      unsubscribe();
      NotificationManager.disconnect();
    };
  }, [endpoint, headers, withCredentials]);

  useEffect(() => {
    NotificationManager.subscribe((localNotify) => {
      if (localNotify) {
        //@ts-ignore
        setNotifications(localNotify.eventMessage["unreadCount"]);
        setNotficationRecieved(true);
      }
    });
  }, []);

  const updateNotificatiosnRecieved = React.useCallback(() => {
    setNotficationRecieved(false);
  }, []);

  if (!NOTIFICATIONS_ENABLED) {
    return <DisabledNotificationProvider>{children}</DisabledNotificationProvider>;
  }

  return (
    <NotificationContext.Provider
      value={{ connected, notifications, notificationReceived: notficationRecieved, updateNotificatiosnRecieved }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error("useNotifications must be used within a NotificationProvider");
  return context;
};
export const useNotificationStream = (handler: (notification: Notification) => void, subjects: string[] = []) => {
  const { connected } = useNotifications();
  // Create a ref to store the handler function
  const handlerRef = useRef(handler);
  // Update the ref when the handler changes
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);
  // Subscribe to notifications on mount, unsubscribe on unmount
  useEffect(() => {
    // Create a wrapper that calls the current handler from the ref
    const handlerWrapper = (notification: Notification) => {
      handlerRef.current(notification);
    };
    // Subscribe to the notification manager
    const unsubscribe = NotificationManager.subscribe(handlerWrapper, subjects);
    // Unsubscribe on unmount
    return unsubscribe;
    // Subjects is included in dependencies because we want to resubscribe if they change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjects]);
  // Return connection status
  return { connected };
};
export default NotificationProvider;
